import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Card,
  Button,
  Alert,
  Space,
  Dropdown,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Typography,
  message,
  Progress,
  Statistic,
  Tooltip,
  theme,
} from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import EquipmentFormModal from '../components/EquipmentFormModal';
import { EquipmentDetailModal } from '../components/equipment/EquipmentDetailModal';
import { useAuthStore } from '../stores/authStore';
import { CategoryTag } from '../components/common/CategoryTag';
import { CategoryTagCloudFilter } from '../components/common/CategoryTagCloudFilter';
import { featureFlags } from '../utils/featureFlags';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  MoreOutlined,
  EyeOutlined,
  AppstoreOutlined,
  SettingOutlined,
  CopyOutlined,
  ToolOutlined,
  CalendarOutlined,
  LineChartOutlined,
  BellOutlined,
  LockOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { getErrorMessage } from '../utils/errorMessage';
import { NameLink, PageHeader, PageContainer, withDevModalFilePath } from '../components/common';
import { SUBSCRIPTION_PLAN_CONFIGS } from '@shared/config/subscription-plans';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';
import { HelpButton } from '../components/help/HelpButton';
import { usePermission } from '../hooks/usePermission';
import { PermissionButton } from '../components/common/PermissionButton';
import { hasRequiredHealthParameters } from './utils/healthParameterVisibility';
import { getMutationPendingState } from '../utils/trpcMutation';
import {
  DEFAULT_EQUIPMENT_LIST_SORT,
  EQUIPMENT_LIST_SORT_STORAGE_KEY,
  getSortOrderLabel,
  loadSortPreference,
  persistSortPreference,
  type EquipmentListSortField,
  type SortPreference,
} from './utils/sortPreferences';
import { isEulType } from '../constants/eul-types';

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/EquipmentsPage.tsx');

type EquipmentCategoryRef = {
  id?: string | null;
  categoryKey?: string | null;
  categoryLabel?: string | null;
  description?: string | null;
  displayOrder?: number | null;
  color?: string | null;
  icon?: string | null;
  isActive?: boolean | null;
};

type EquipmentCategoryWithUsage = {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
  usageCount?: number | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeEquipmentCategoryList = (value: unknown): EquipmentCategoryWithUsage[] => {
  const categories =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : isRecord(value) && Array.isArray(value.data)
          ? value.data
          : [];

  return categories.flatMap((category) => {
    if (!isRecord(category) || typeof category.id !== 'string') {
      return [];
    }

    return [{
      id: category.id,
      categoryLabel: typeof category.categoryLabel === 'string' ? category.categoryLabel : category.id,
      color: typeof category.color === 'string' ? category.color : null,
      icon: typeof category.icon === 'string' ? category.icon : null,
      usageCount: typeof category.usageCount === 'number' ? category.usageCount : 0,
    }];
  });
};

const normalizeEquipmentLocationIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.locationId !== 'string') {
      return [];
    }
    return [item.locationId];
  });
};

interface Equipment {
  id: string;
  parentId?: string | null;
  mentenaPathNum?: string | null;
  displayId: string;
  name: string;
  category: string | EquipmentCategoryRef | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  dpEul?: number | null;
  dpEulInput?: number | null;
  dpEulType?: string | null;
  dpKEnv?: number | null;
  dpKMaint?: number | null;
  dpKMat?: number | null;
  dpKConst?: number | null;
  dpBeta?: number | null;
  dpBaseDate?: string | null;
  dpEquipmentType?: string | null;
  // dpMonitoringEnabled removed (dp_monitoring system deprecated)
  // dpMonitoringNextChangeableAt removed (dp_monitoring system deprecated)
  manufacturerLifespan?: number | null; // メーカー耐用年数（年）
  commissionedDate?: string | null; // 稼働開始日（統合カラム）
  installationDate?: string | null;
  startDate?: string | null;
  price?: number | null;
  userId?: string | null;
  currentDeteriorationRate?: number | null;
  importanceLevel: number | null; // 重要度: 1:低, 2:中, 3:高, 4:最重要
  operatingHours?: number | null;
  lastInspectionDate?: string | null;
  nextMaintenanceDate?: string | null;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  // optional child equipment count from API
  childCount?: number;
  childRiskSummary?: {
    below50Count: number;
    below70Count: number;
    lowestHealthIndex: number | null;
  };
}

type EquipmentTableRecord = Equipment & {
  level?: number;
  hasChildren?: boolean;
};

type EquipmentFormInitialValues = NonNullable<ComponentProps<typeof EquipmentFormModal>['initialValues']>;

const resolveEquipmentCategoryId = (
  category: Equipment['category']
): string | undefined => {
  if (typeof category === 'string') {
    return category;
  }
  if (category && typeof category === 'object') {
    return typeof category.id === 'string' ? category.id : undefined;
  }
  return undefined;
};

export function EquipmentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = theme.useToken();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    searchParams.get('category') || undefined
  );
  const [selectedImportanceLevel, setSelectedImportanceLevel] = useState<number | undefined>();
  const includeArchived = false;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortPreference, setSortPreference] = useState<SortPreference<EquipmentListSortField>>(() =>
    loadSortPreference(
      EQUIPMENT_LIST_SORT_STORAGE_KEY,
      ['displayId', 'health', 'importanceLevel'] as const,
      DEFAULT_EQUIPMENT_LIST_SORT,
    )
  );
  const [viewingEquipmentId, setViewingEquipmentId] = useState<string | null>(null);
  const [selectedEquipments, setSelectedEquipments] = useState<string[]>([]);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [, modalContextHolder] = Modal.useModal();
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? undefined;
  const canQueryCompanyScopedData = Boolean(companyId);
  const { canEdit, getDisabledMessage } = usePermission();
  const editDisabledMessage = getDisabledMessage('edit');

  useEffect(() => {
    persistSortPreference(EQUIPMENT_LIST_SORT_STORAGE_KEY, sortPreference);
  }, [sortPreference]);

  const categoryFilterValue =
    selectedCategory !== undefined && selectedCategory !== null && String(selectedCategory).trim() !== ''
      ? String(selectedCategory)
      : undefined;

  const normalizedImportanceLevel =
    selectedImportanceLevel !== undefined && selectedImportanceLevel !== null
      ? Number(selectedImportanceLevel)
      : undefined;

  const importanceLevelFilter =
    normalizedImportanceLevel !== undefined && Number.isFinite(normalizedImportanceLevel)
      ? normalizedImportanceLevel
      : undefined;

  // APIからデータ取得（階層表示対応）
  const { data: equipmentData, isLoading, refetch } = trpc.equipment.getListWithHierarchy.useQuery({
    pagination: {
      page: currentPage,
      pageSize: pageSize,
    },
    filter: {
      search: searchText || undefined,
      category: categoryFilterValue,
      importanceLevel: importanceLevelFilter,
      isActive: true,
      includeArchived: includeArchived
    },
    sort: sortPreference,
  }, {
    enabled: canQueryCompanyScopedData
  });

  // 会社情報取得（プランと設備数上限の計算用）
  const { data: company } = trpc.company.getCurrentCompany.useQuery(undefined, {
    enabled: canQueryCompanyScopedData,
  });
  const { data: healthManagementMode } = trpc.company.getHealthManagementMode.useQuery(undefined, {
    enabled: canQueryCompanyScopedData,
  });

  const equipments = equipmentData?.data || [];
  const total = equipmentData?.pagination?.total || 0;

  // 設備数上限の計算
  const calculateEquipmentLimit = () => {
    if (!company) return null;

    const plan = company.subscriptionPlan as ManagedPlanCode;
    const planConfig = SUBSCRIPTION_PLAN_CONFIGS[plan];

    if (!planConfig) return null;

    const baseLimit = planConfig.equipmentLimit;
    if (baseLimit === null) return null; // 無制限

    const addonUnits = company.subscriptionAddonUnits || 0;
    const addonEquipments = addonUnits * 50; // 50台/ユニット

    return {
      baseLimit,
      addonEquipments,
      totalLimit: baseLimit + addonEquipments,
      currentCount: total,
      usagePercent: baseLimit + addonEquipments > 0
        ? (total / (baseLimit + addonEquipments)) * 100
        : 0,
    };
  };

  const equipmentLimit = calculateEquipmentLimit();

  // 上限チェック
  const isAtCapacity = equipmentLimit
    ? total >= equipmentLimit.totalLimit
    : false;

  // フィルタリング中かどうかを判定
  const isFiltering = Boolean(selectedCategory || selectedImportanceLevel !== undefined);

  // エリアツリーデータ取得
  trpc.location.getTree.useQuery(undefined, { enabled: canQueryCompanyScopedData });

  // カテゴリ一覧取得（使用数付き: AVS-347対応）
  const { data: categoriesData } = trpc.equipmentCategory.listWithUsage.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  }, {
    enabled: canQueryCompanyScopedData
  });
  const categories = useMemo(
    () => normalizeEquipmentCategoryList(categoriesData),
    [categoriesData]
  );

  // カテゴリマップ作成（categoryId → categoryLabel）
  const categoryMap = categories.reduce((acc, cat) => {
    if (!cat) {
      return acc;
    }

    const label =
      typeof cat.categoryLabel === 'string' && cat.categoryLabel.trim().length > 0
        ? cat.categoryLabel
        : undefined;

    if (cat.id) {
      const idKey = String(cat.id);
      acc[idKey] = label ?? idKey;
    }

    return acc;
  }, {} as Record<string, string>);



  // 作成・更新用のミューテーション
  const createMutation = trpc.equipment.create.useMutation({
    onSuccess: () => {
      messageApi.success(`設備を登録しました`);
      setIsModalVisible(false);
      if (canQueryCompanyScopedData) {
        refetch();
      }
    },
    onError: (error: unknown) => {
      messageApi.error(`登録に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  });

  const updateMutation = trpc.equipment.update.useMutation({
    onSuccess: () => {
      messageApi.success(`設備を更新しました`);
      setIsModalVisible(false);
      setEditingEquipment(null);
      if (canQueryCompanyScopedData) {
        refetch();
      }
    },
    onError: (error: unknown) => {
      messageApi.error(`更新に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  });

  // エリア取得用のクエリ
  const { data: equipmentLocations } = trpc.equipment.getLocationsByEquipmentId.useQuery(
    {
      equipmentId: editingEquipment?.id || '',
      ...(companyId ? { companyId } : {}),
    },
    { enabled: canQueryCompanyScopedData && !!editingEquipment?.id }
  );
  const equipmentLocationIds = useMemo(
    () => normalizeEquipmentLocationIds(equipmentLocations),
    [equipmentLocations]
  );
  const equipmentFormInitialValues = useMemo<EquipmentFormInitialValues | null>(() => {
    if (!editingEquipment) {
      return {
        locationIds: equipmentLocationIds,
      };
    }

    return {
      id: editingEquipment.id,
      displayId: editingEquipment.displayId,
      parentId: editingEquipment.parentId ?? null,
      locationIds: equipmentLocationIds,
      name: editingEquipment.name,
      category: resolveEquipmentCategoryId(editingEquipment.category) ?? null,
      model: editingEquipment.model ?? null,
      serialNumber: editingEquipment.serialNumber ?? null,
      dpEulType: isEulType(editingEquipment.dpEulType) ? editingEquipment.dpEulType : null,
      dpEulInput: editingEquipment.dpEulInput ?? null,
      dpEul: editingEquipment.dpEul ?? null,
      dpKMaint: editingEquipment.dpKMaint ?? null,
      dpKConst: editingEquipment.dpKConst ?? null,
      dpBeta: editingEquipment.dpBeta ?? null,
      dpKEnv: editingEquipment.dpKEnv ?? null,
      dpKMat: editingEquipment.dpKMat ?? null,
      manufacturerLifespan: editingEquipment.manufacturerLifespan ?? null,
      price: editingEquipment.price ?? null,
      importanceLevel: editingEquipment.importanceLevel ?? null,
      commissionedDate: editingEquipment.commissionedDate ?? null,
      installationDate: editingEquipment.installationDate ?? null,
      notes: editingEquipment.notes ?? null,
    };
  }, [editingEquipment, equipmentLocationIds]);

  // エリア更新用のミューテーション
  const updateLocationsMutation = trpc.equipment.updateEquipmentLocations.useMutation({
    onSuccess: () => {
      messageApi.success('エリアを更新しました');
    },
    onError: (error: unknown) => {
      messageApi.error(`エリア更新に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  });

  // 複製用のミューテーション
  const duplicateMutation = trpc.equipment.duplicate.useMutation({
    onSuccess: (data) => {
      messageApi.success(`設備を複製しました: ${data.name}`);
      if (canQueryCompanyScopedData) {
        refetch();
      }
      // 複製した設備の詳細ページへ遷移
      navigate(`/app/equipments/${data.id}`);
    },
    onError: (error: unknown) => {
      messageApi.error(`複製に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  });

  // 検索条件が変わったらページを1に戻す
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCategory, selectedImportanceLevel, sortPreference]);

  // 旧ダミーデータ（コメントアウト）
  /* const equipments: Equipment[] = [
    {
      id: '1',
      displayId: 'E-00001',
      name: 'ポンプA-101',
      category: 'ポンプ',
      manufacturer: '山田製作所',
      model: 'YP-500',
      serialNumber: 'SN-2020-001',
      startDate: '2020-04-15',
      commissionedDate: '2020-04-15',
      currentDeteriorationRate: 35,
      riskLevel: 1, // low
      operatingHours: 8760,
      isActive: true,
      nextMaintenanceDate: '2024-04-15',
      createdAt: '2020-04-15T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z'
    },
    {
      id: '2',
      displayId: 'E-00002',
      name: 'モーターB-201',
      category: 'モーター',
      manufacturer: '田中工業',
      model: 'TM-300',
      serialNumber: 'SN-2019-002',
      startDate: '2019-06-20',
      commissionedDate: '2019-06-20',
      currentDeteriorationRate: 72,
      riskLevel: 3, // high
      operatingHours: 12500,
      isActive: true,
      nextMaintenanceDate: '2024-02-20',
      createdAt: '2019-06-20T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z'
    },
    {
      id: '3',
      displayId: 'E-00003',
      name: 'コンプレッサーC-301',
      category: 'コンプレッサー',
      manufacturer: '鈴木製作所',
      model: 'SC-800',
      serialNumber: 'SN-2018-003',
      startDate: '2018-03-10',
      commissionedDate: '2018-03-10',
      currentDeteriorationRate: 85,
      riskLevel: 4, // critical
      operatingHours: 15000,
      isActive: true,
      nextMaintenanceDate: '2024-01-10',
      createdAt: '2018-03-10T00:00:00.000Z',
      updatedAt: '2024-01-01T00:00:00.000Z'
    }
  ]; */

  const getRiskColor = (level: number | null) => {
    switch (level) {
      case 1: return 'green';
      case 2: return 'orange';
      case 3: return 'red';
      case 4: return 'purple';
      default: return 'default';
    }
  };

  const getRiskText = (level: number | null) => {
    switch (level) {
      case 1: return '低';
      case 2: return '中';
      case 3: return '高';
      case 4: return '要対応';
      default: return '-';
    }
  };

  const renderChildRiskSummary = (record: EquipmentTableRecord) => {
    const summary = record.childRiskSummary;
    if (!summary) {
      return null;
    }

    const hasSummary =
      summary.lowestHealthIndex !== null ||
      summary.below50Count > 0 ||
      summary.below70Count > 0;

    if (!hasSummary) {
      return null;
    }

    return (
      <Space size={[4, 4]} wrap>
        {summary.lowestHealthIndex !== null && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            配下最小健全度 {summary.lowestHealthIndex.toFixed(1)}%
          </Text>
        )}
        {summary.below50Count > 0 && (
          <Tag color="error" style={{ marginInlineEnd: 0 }}>
            健全度50%以下 {summary.below50Count}台
          </Tag>
        )}
        {summary.below50Count === 0 && summary.below70Count > 0 && (
          <Tag color="warning" style={{ marginInlineEnd: 0 }}>
            健全度70%以下 {summary.below70Count}台
          </Tag>
        )}
      </Space>
    );
  };

  const columns: ResponsiveColumnType<EquipmentTableRecord>[] = [
    {
      title: `設備名`,
      dataIndex: 'name',
      key: 'name',
      width: 180,
      mobileRole: 'title',
      mobilePriority: 1,
      render: (name: string, record: EquipmentTableRecord) => {
        const indentWidth = (record.level || 0) * 24; // 24px per level
        const hasChildren = record.hasChildren || record.childCount > 0;

        return (
          <div
            style={{
              paddingLeft: `${indentWidth}px`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {/* 階層インジケータ */}
            {record.level > 0 && (
              <span style={{ color: token.colorTextQuaternary, fontFamily: 'monospace' }}>
                └─
              </span>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <NameLink name={name} onClick={() => navigate(`/app/equipments/${record.id}`)} />

                {hasChildren && (
                  <Tag color="blue" style={{ marginLeft: 4 }}>
                    {record.childCount}台
                  </Tag>
                )}
              </div>
              {renderChildRiskSummary(record)}
            </div>
          </div>
        );
      },
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      width: 80,
      key: 'category',
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (category: Equipment["category"]) => (
        <CategoryTag
          category={category}
          linkPath="/app/equipments"
          categoryMap={categoryMap}
        />
      ),
    },
    {
      title: '健全度',
      dataIndex: 'currentDeteriorationRate',
      key: 'currentDeteriorationRate',
      width: 80,
      mobileRole: 'description',
      mobilePriority: 3,
      render: (rate: number | string | null, record: Equipment) => {
        const hasRequiredParams = hasRequiredHealthParameters(healthManagementMode, {
          dpEul: record.dpEul,
          dpBeta: record.dpBeta,
          manufacturerLifespan: record.manufacturerLifespan,
          commissionedDate: record.commissionedDate,
          installationDate: record.installationDate,
          startDate: record.startDate,
        });

        if (!hasRequiredParams) {
          return <span style={{ color: '#999' }}>???%</span>;
        }

        // rate が null/undefined の場合
        if (rate === null || rate === undefined) {
          return <span style={{ color: '#999' }}>???%</span>;
        }

        const rateValue = Number(rate);
        if (!Number.isFinite(rateValue)) {
          return <span style={{ color: '#999' }}>???%</span>;
        }
        const healthIndex = 100 - rateValue;
        const textColor = healthIndex <= 50 ? '#ff4d4f' : healthIndex <= 70 ? '#faad14' : undefined;

        return (
          <Progress
            percent={healthIndex}
            size="small"
            strokeColor={{
              '0%': '#ff4d4f',
              '50%': '#faad14',
              '100%': '#52c41a'
            }}
            format={(percent) => (
              <span style={{ color: textColor, fontWeight: textColor ? 'bold' : 'normal' }}>
                {percent?.toFixed(1)}%
              </span>
            )}
          />
        );
      },
    },
    {
      title: '重要度',
      dataIndex: 'importanceLevel',
      key: 'importanceLevel',
      width: 60,
      mobileRole: 'tag',
      mobilePriority: 4,
      render: (level: number | null) => (
        <Tag color={getRiskColor(level)}>{getRiskText(level)}</Tag>
      ),
    },
    {
      title: 'ステータス',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      mobileRole: 'extra',
      mobilePriority: 5,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? '稼働中' : '停止中'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 40,
      fixed: 'right', //これがあるとtableのtable-layout:fixedになる
      mobileRole: 'hidden',
      render: (_value: unknown, record: Equipment) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'viewModal',
            label: '詳細表示',
            icon: <EyeOutlined />,
          },
          { type: 'divider' as const },
          {
            key: 'edit',
            label: canEdit ? '編集' : (
              <Tooltip title={editDisabledMessage}>
                <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>編集</span>
              </Tooltip>
            ),
            icon: canEdit ? <EditOutlined /> : <LockOutlined />,
            disabled: !canEdit,
          },
          {
            key: 'duplicate',
            label: canEdit ? '複製' : (
              <Tooltip title={editDisabledMessage}>
                <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>複製</span>
              </Tooltip>
            ),
            icon: canEdit ? <CopyOutlined /> : <LockOutlined />,
            disabled: !canEdit,
          },
        ];
        return (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: ({ key }) => handleActionMenuClick(key as string, record),
            }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Button icon={<MoreOutlined />} size="small" aria-label="more" />
          </Dropdown>
        );
      },
    },
  ];


  const handleEdit = (equipment: Equipment) => {
    setEditingEquipment(equipment);
    setIsModalVisible(true);
  };

  const handleActionMenuClick = (actionKey: string, record: Equipment) => {
    switch (actionKey) {
      case 'viewModal':
        setViewingEquipmentId(record.id);
        break;
      case 'viewPage':
        navigate(`/app/equipments/${record.id}`);
        break;
      case 'edit':
        handleEdit(record);
        break;
      case 'duplicate':
        duplicateMutation.mutate({ id: record.id });
        break;
      default:
        break;
    }
  };

  // 編集時にエリア情報を取得してフォームに設定
  // 共通モーダル提出処理
  const handleModalSubmit = async ({
    values,
    locationIds,
  }: {
    values: Record<string, unknown>;
    locationIds: string[];
  }) => {
    if (editingEquipment) {
      await updateMutation.mutateAsync({ id: editingEquipment.id, data: values });
      await updateLocationsMutation.mutateAsync({ equipmentId: editingEquipment.id, locationIds: locationIds || [] });
    } else {
      createMutation.mutate({ ...values, locationIds: locationIds || [] });
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingEquipment(null);
  };

  const handleDetailModalClose = () => {
    setViewingEquipmentId(null);
  };

  // フィルタリングはAPIサイドで行われるため、ローカルフィルタリングは不要
  const rowSelection: TableProps<Equipment>['rowSelection'] = {
    selectedRowKeys: selectedEquipments,
    onChange: (keys) => setSelectedEquipments(keys.map((key) => String(key))),
    columnWidth: 60,
  };

  return (
    <PageContainer>
      {messageContextHolder}
      {modalContextHolder}
      <PageHeader
        title="設備管理"
        subtitle="設備の登録・編集と保全スケジュールの管理ができます。"
        data-testid="equipment-page-title"
        extra={
          <Space>
            <HelpButton topicId="equipment-overview" size="middle" position="inline" />
            <PermissionButton
              icon={<SettingOutlined />}
              onClick={() => navigate('/app/categories#equipment')}
              title="設備カテゴリ管理"
              requiredPermission="manage"
            >
              カテゴリ管理
            </PermissionButton>
            {featureFlags.qrLabelPrinting && (
              <Button
                icon={<QrcodeOutlined />}
                onClick={() => navigate('/app/qr-labels#equipment')}
              >
                QRコードシール印刷
              </Button>
            )}
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (isAtCapacity) {
                  messageApi.warning('設備登録数が上限に達しています。追加オプションを追加してください。');
                  return;
                }
                setEditingEquipment(null);
                setIsModalVisible(true);
              }}
              disabled={isAtCapacity}
              requiredPermission="edit"
              data-testid="equipment-add-button"
            >
              新規登録
            </PermissionButton>
            {isAtCapacity && (
              <Text type="danger" style={{ fontSize: 12 }}>
                上限到達
              </Text>
            )}
          </Space>
        }
      />

      {/* 空状態UI: 設備が0件の場合 - ヘッダー直下に機能説明カードを表示 */}
      {equipments.length === 0 && !isLoading && !isFiltering && !searchText && (
        <Card
          style={{
            marginBottom: 16,
            background: token.colorWarningBg,
            border: `1px solid ${token.colorWarningBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <ToolOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                設備を登録しましょう
              </Title>
              <Text type="secondary">
                設備を登録すると、以下の機能が利用できるようになります。
              </Text>
            </Col>
            <Col>
              <PermissionButton
                type="primary"
                onClick={() => {
                  setEditingEquipment(null);
                  setIsModalVisible(true);
                }}
                requiredPermission="edit"
              >
                設備を登録する
              </PermissionButton>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <CalendarOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                  <div>
                    <Text strong>保全スケジュール</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      点検・メンテナンス計画を立てて管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <LineChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>健全度予測</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      設備の劣化状況を予測・把握
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <BellOutlined style={{ fontSize: 20, color: token.colorInfo }} />
                  <div>
                    <Text strong>通知・アラート</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      点検時期の通知を自動で受け取る
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 設備数上限表示（フィルタリング中は非表示） */}
      {equipmentLimit && !isFiltering && (
        <Card
          style={{ marginBottom: 16 }}
          styles={{
            body: {
              backgroundColor: isAtCapacity ? token.colorErrorBg : undefined,
            },
          }}
        >
          {isAtCapacity && (
            <Alert
              type="error"
              message="設備登録数が上限に達しています"
              description="新しい設備を登録するには、追加オプションを追加してください。"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="登録設備数"
                value={equipmentLimit.currentCount}
                suffix={`/ ${equipmentLimit.totalLimit}台`}
                prefix={<AppstoreOutlined />}
              />
              <Progress
                percent={equipmentLimit.usagePercent}
                status={equipmentLimit.usagePercent >= 95 ? 'exception' : equipmentLimit.usagePercent >= 80 ? 'active' : 'success'}
                size="small"
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="基本上限"
                value={equipmentLimit.baseLimit}
                suffix="台"
              />
              {equipmentLimit.addonEquipments > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  追加オプション: +{equipmentLimit.addonEquipments}台
                </Text>
              )}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="残り登録可能"
                value={Math.max(0, equipmentLimit.totalLimit - equipmentLimit.currentCount)}
                suffix="台"
                valueStyle={{
                  color: equipmentLimit.usagePercent >= 90 ? '#cf1322' : '#3f8600',
                }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              placeholder={`設備名・IDで検索`}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              data-testid="equipment-search-input"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="重要度"
              allowClear
              style={{ width: '100%' }}
              value={selectedImportanceLevel}
              onChange={setSelectedImportanceLevel}
              data-testid="equipment-importance-filter"
            >
              <Select.Option value={1}>低</Select.Option>
              <Select.Option value={2}>中</Select.Option>
              <Select.Option value={3}>高</Select.Option>
              <Select.Option value={4}>要対応</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.field}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  field: value as EquipmentListSortField,
                }))
              }
              data-testid="equipment-sort-field-select"
            >
              <Select.Option value="displayId">表示ID</Select.Option>
              <Select.Option value="health">健全度</Select.Option>
              <Select.Option value="importanceLevel">重要度</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.order}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  order: value as SortPreference<EquipmentListSortField>['order'],
                }))
              }
              data-testid="equipment-sort-order-select"
            >
              <Select.Option value="asc">{getSortOrderLabel(sortPreference.field, 'asc')}</Select.Option>
              <Select.Option value="desc">{getSortOrderLabel(sortPreference.field, 'desc')}</Select.Option>
            </Select>
          </Col>
        </Row>

        {/* カテゴリタグクラウドフィルタ */}
        <CategoryTagCloudFilter
          categories={categories.map(cat => ({
            id: cat.id,
            categoryLabel: cat.categoryLabel || cat.id,
            color: cat.color,
            icon: cat.icon,
            // AVS-347: usageCountを使用（カテゴリ選択時も正確な件数を表示）
            count: cat.usageCount ?? 0,
          }))}
          selectedCategory={selectedCategory}
          onCategoryChange={(categoryId) => setSelectedCategory(categoryId)}
          loading={!categories.length && isLoading}
          showCounts={true}
          maxVisibleTags={15}
        />
      </Card>

      <Card>
        {equipments.length === 0 && !isLoading && !isFiltering && !searchText ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Text type="secondary">上のカードから設備を登録してください</Text>
          </div>
        ) : (
          <ResponsiveTable
            columns={columns}
            dataSource={equipments || []}
            rowKey="id"
            rowSelection={rowSelection}
            loading={isLoading}
            scrollX={600}
            mobileCardConfig={{
              titleField: (record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <a
                    onClick={() => navigate(`/app/equipments/${record.id}`)}
                    style={{ color: token.colorLink, fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    {record.name}
                  </a>
                  {renderChildRiskSummary(record)}
                </div>
              ),
              actions: (record) => (
                <Dropdown
                  menu={{
                    items: [
                      { key: 'viewModal', label: '詳細表示', icon: <EyeOutlined /> },
                      { type: 'divider' as const },
                      { key: 'edit', label: '編集', icon: <EditOutlined /> },
                      { key: 'duplicate', label: '複製', icon: <CopyOutlined /> },
                    ],
                    onClick: ({ key }) => handleActionMenuClick(key as string, record),
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <Button icon={<MoreOutlined />} size="small" aria-label="more" />
                </Dropdown>
              ),
              onClick: (record) => navigate(`/app/equipments/${record.id}`),
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showTotal: (total) => `全 ${total} 件`,
              position: ['topRight', 'bottomRight'],
              onChange: (page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize || 50);

              },
              onShowSizeChange: (current, size) => {
                void current; // mark parameter as used
                setCurrentPage(1);
                setPageSize(size);

              }
            }}
          />
        )}
      </Card>

      <EquipmentDetailModal open={!!viewingEquipmentId} equipmentId={viewingEquipmentId} onClose={handleDetailModalClose} />

      <EquipmentFormModal
        open={isModalVisible}
        title={editingEquipment ? `設備編集` : `設備登録`}
        data-testid="equipment-form-modal"
        currentId={editingEquipment?.id || null}
        confirmLoading={getMutationPendingState(createMutation) || getMutationPendingState(updateMutation) || getMutationPendingState(updateLocationsMutation)}
        initialValues={equipmentFormInitialValues}
        onCancel={handleModalCancel}
        onSubmit={async ({ values, locationIds }) => {
          await handleModalSubmit({ values, locationIds });
          handleModalCancel();
        }}
      />
    </PageContainer>
  );
}
